// Import des factures de vente (Dolibarr) — lot B, 11/10/2026.
// Une facture par PDF : PDF sélectionnés et/ou ZIP (ouvert dans le
// navigateur), envoyés UN PAR UN au serveur avec progression et résultat
// par fichier. Export Dolibarr (CSV/Excel) facultatif : chargé en premier,
// ses données font foi (sans IA) et chaque PDF y est rattaché par numéro.
// Pause / reprise (10/10/2026) : la reprise repart du premier fichier non
// traité ; après fermeture, resélectionner le ZIP suffit (les factures déjà
// importées sont reconnues). Plafond quotidien d'IA atteint → pause auto.
import { useRef, useState } from "react";
import JSZip from "jszip";
import api from "../services/api";

const STATUS = {
  pending:  { label: "en attente", color: "#6b7280" },
  running:  { label: "lecture…", color: "#6b7280" },
  imported: { label: "✓ importée, à valider", color: "#1a9e72" },
  attached: { label: "✓ PDF rattaché à l'export", color: "#1a9e72" },
  already:  { label: "✓ déjà importée", color: "#6b7280" },
  warning:  { label: "⚠ à vérifier", color: "#ba7517" },
  refused:  { label: "✗ refusée", color: "#e24b4a" },
  error:    { label: "✗ erreur", color: "#e24b4a" },
};

const toBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(file);
});

export default function SalesImportModal({ companyId, onClose, onDone }) {
  const [files, setFiles] = useState([]);       // PDF à envoyer (File)
  const [exportFile, setExportFile] = useState(null);
  const [rows, setRows] = useState([]);         // { name, status, message }
  const [exportResult, setExportResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState("");
  const pauseRef = useRef(false);
  const listRef = useRef([]);
  const batchRef = useRef(null);

  async function pick(e) {
    setError("");
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const pdfs = [];
    for (const f of picked) {
      if (/\.zip$/i.test(f.name)) {
        try {
          const zip = await JSZip.loadAsync(f);
          const entries = Object.values(zip.files).filter(z => !z.dir && /\.pdf$/i.test(z.name) && !/(^|\/)__MACOSX\//.test(z.name));
          for (const z of entries) {
            const blob = await z.async("blob");
            pdfs.push(new File([blob], z.name.split("/").pop(), { type: "application/pdf" }));
          }
        } catch { setError(`ZIP illisible : ${f.name}`); }
      } else if (/\.(csv|xlsx|xls)$/i.test(f.name)) {
        setExportFile(f);
      } else if (/\.pdf$/i.test(f.name) || f.type === "application/pdf") {
        pdfs.push(f);
      }
    }
    setFiles(prev => [...prev, ...pdfs]);
    setRows([]); listRef.current = [];
  }

  // Envoie les fichiers encore « en attente », un par un, jusqu'à la fin ou la pause
  async function sendPending() {
    setRunning(true); setPaused(false); pauseRef.current = false;
    const list = listRef.current;
    for (let i = 0; i < files.length; i++) {
      if (list[i].status !== "pending") continue;
      if (pauseRef.current) { setPaused(true); break; }
      list[i] = { ...list[i], status: "running" }; setRows([...list]);
      try {
        const r = await api.salesOcrInvoices.dolibarrUpload(companyId, files[i], batchRef.current, { withExport: !!exportFile });
        const disc = r.discrepancies && r.discrepancies.length;
        list[i] = {
          ...list[i],
          status: disc ? "warning" : r.status === "attached" ? "attached" : (r.match_status === "to_confirm" ? "warning" : "imported"),
          message: [r.invoice_ref, r.client_name, r.amount_ttc != null ? `${Number(r.amount_ttc).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €` : null,
            r.match_status === "to_confirm" ? "client à confirmer" : null, disc ? `écart : ${r.discrepancies.join(" ; ")}` : null].filter(Boolean).join(" · "),
        };
      } catch (e) {
        if (e.code === "AI_DAILY_QUOTA" || e.status === 429) {
          // Plafond quotidien d'IA ou trop de requêtes : ce fichier reste à envoyer, import en pause
          list[i] = { ...list[i], status: "pending" };
          setError(e.message); pauseRef.current = true; setRows([...list]); setPaused(true);
          break;
        }
        list[i] = {
          ...list[i],
          status: e.code === "DUPLICATE_INVOICE" ? "already"
            : ["MERGED_PDF", "NOT_AN_INVOICE", "PRIOR_EXERCISE", "NOT_IN_EXPORT"].includes(e.code) ? "refused" : "error",
          message: e.message,
        };
      }
      setRows([...list]);
    }
    setRunning(false);
    if (!list.some(r => r.status === "pending")) setPaused(false);
    onDone && onDone();
  }

  async function run() {
    setError(""); setExportResult(null);
    batchRef.current = (window.crypto?.randomUUID?.() || `lot-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
    try {
      if (exportFile) {
        setRunning(true);
        const r = await api.salesOcrInvoices.dolibarrExport(companyId, { content: await toBase64(exportFile), filename: exportFile.name, batch_id: batchRef.current });
        setExportResult(r);
      }
    } catch (e) {
      setError(`Export : ${e.message}`); setRunning(false); return;
    }
    listRef.current = files.map(f => ({ name: f.name, status: "pending", message: "" }));
    setRows([...listRef.current]);
    await sendPending();
  }

  const done = rows.filter(r => !["pending", "running"].includes(r.status)).length;
  const count = (s) => rows.filter(r => r.status === s).length;
  const started = rows.length > 0;

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="card" style={{ width: 720, maxWidth: "96vw", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Importer des factures de vente</div>
          <button className="btn" onClick={onClose} disabled={running}>✕</button>
        </div>
        <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 12, lineHeight: 1.5 }}>
          <strong>Une facture par PDF</strong> (un PDF regroupant plusieurs factures est refusé). Sélectionnez les PDF ou un ZIP.
          Factures (FA), factures d'acompte (AC) et avoirs à partir de septembre 2026 ; les commandes (CO) ne sont pas importées.
          Recommandé : ajoutez l'<strong>export Dolibarr</strong> des factures (CSV ou Excel) — ses données font alors foi, sans lecture par l'IA, et chaque PDF y est rattaché par son numéro.
          Vous pouvez mettre l'import en pause et le reprendre ; après une fermeture, resélectionnez le ZIP : les factures déjà importées sont reconnues.
        </div>
        {!started && (
          <label className="btn primary" style={{ alignSelf: "flex-start", cursor: running ? "wait" : "pointer", marginBottom: 10 }}>
            + Choisir des PDF, un ZIP ou l'export Dolibarr
            <input type="file" multiple accept=".pdf,.zip,.csv,.xlsx,.xls,application/pdf" onChange={pick} disabled={running} style={{ display: "none" }} />
          </label>
        )}
        <div style={{ fontSize: 12.5, marginBottom: 8 }}>
          {files.length} PDF sélectionné(s){exportFile ? ` · export : ${exportFile.name}` : ""}
          {!running && !started && (files.length > 0 || exportFile) && <button className="btn" style={{ fontSize: 11, padding: "2px 8px", marginLeft: 8 }} onClick={() => { setFiles([]); setExportFile(null); setRows([]); }}>Vider</button>}
        </div>
        {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 10, fontSize: 13 }}>{error}</div>}
        {exportResult && (
          <div style={{ fontSize: 12.5, padding: "8px 12px", background: "#f0fdf9", borderRadius: 8, marginBottom: 10 }}>
            Export : {exportResult.rows} ligne(s) lue(s), {exportResult.created} facture(s) créée(s) « à valider »
            {exportResult.duplicates?.length ? `, ${exportResult.duplicates.length} déjà importée(s)` : ""}
            {exportResult.skipped?.length ? `, ${exportResult.skipped.length} hors périmètre (avant septembre 2026 ou commandes)` : ""}.
          </div>
        )}
        {started && (
          <>
            <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>
              {running ? `Envoi ${done}/${rows.length}…` : paused ? `En pause : ${done}/${rows.length} traité(s)` : `Terminé : ${count("imported") + count("attached")} importée(s), ${count("already")} déjà importée(s), ${count("warning")} à vérifier, ${count("refused")} refusée(s), ${count("error")} en erreur`}
            </div>
            <div style={{ overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
              {rows.map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12, padding: "5px 8px", background: "#f9fafb", borderRadius: 6 }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "40%" }}>{r.name}</span>
                  <span style={{ color: STATUS[r.status].color, textAlign: "right" }}>{STATUS[r.status].label}{r.message ? ` — ${r.message}` : ""}</span>
                </div>
              ))}
            </div>
          </>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button className="btn" onClick={onClose} disabled={running}>Fermer</button>
          {running && <button className="btn" onClick={() => { pauseRef.current = true; }}>⏸ Pause</button>}
          {!running && paused && <button className="btn primary" onClick={() => { setError(""); sendPending(); }}>▶ Reprendre</button>}
          {!started && (
            <button className="btn primary" disabled={running || (!files.length && !exportFile)} onClick={run}>
              {running ? "Import en cours…" : `Importer ${files.length} PDF${exportFile ? " + l'export" : ""}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
